import React, { useEffect, useMemo, useState } from "react";
import { T, SERIF, STATUS } from "../theme/tokens.js";
import { Panel, Empty } from "./ui/index.jsx";
import DrillPanel from "./DrillPanel.jsx";
import { loadPortfolioRows, programSummary, rollupTotals } from "../lib/portfolio.js";
import { fmtK, today } from "../lib/format.js";

/* The hero view for a senior leader.
 *
 * One row per program. The columns are the four questions a senior leader asks
 * every Monday without wanting to dig: where is margin about to leak, what is
 * going to slip and who feels it, where is the money stuck, and what is waiting
 * on a decision. Red floats to the top, the grid sorts and filters, and every
 * number opens the rows it was counted from, because the fastest way to lose a
 * leader's trust is to be caught wrong once with no way to check.
 *
 * Only an admin reaches it; a member is held to their own contracts by the
 * database. Everything is live from the registers, scored by the one standard
 * in calc, so two rows are always comparable.
 */

/* ---- cell pieces ---- */

function Pill({ posture }) {
  return (
    <span style={{ background: posture.color, color: "#fff", borderRadius: 999,
      padding: "3px 10px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>
      {posture.label}</span>
  );
}

/* Three measures on one scale: spend, time, scope. The gap between them is the
   leading indicator, so they are stacked to be read against each other. */
function PaceCell({ pace }) {
  if (pace.scopePct === null && pace.spentPct === null) {
    return <span style={{ fontSize: 11.5, color: T.soft }}>no data</span>;
  }
  const tone = pace.level === "red" ? STATUS.overdue.ink
    : pace.level === "amber" ? STATUS.inProgress.ink : T.soft;
  const rows = [
    ["Spend", pace.spentPct, T.navy],
    ["Time", pace.timePct, T.steel],
    ["Scope", pace.scopePct, STATUS.complete.fill],
  ];
  return (
    <div style={{ display: "grid", gap: 3, minWidth: 128 }}>
      {rows.map(([label, v, color]) => (
        <div key={label} style={{ display: "grid", gridTemplateColumns: "34px 1fr 30px",
          gap: 5, alignItems: "center" }}>
          <span style={{ fontSize: 9.5, color: T.soft, textTransform: "uppercase",
            letterSpacing: "0.04em" }}>{label}</span>
          <div style={{ background: T.chip, borderRadius: 999, height: 6, overflow: "hidden" }}>
            <div style={{ width: `${Math.max(0, Math.min(100, v ?? 0))}%`, height: "100%",
              background: v === null ? "transparent" : color }} />
          </div>
          <span style={{ fontSize: 10.5, color: T.ink, textAlign: "right",
            fontVariantNumeric: "tabular-nums" }}>{v === null ? "-" : `${v}%`}</span>
        </div>
      ))}
      {pace.level !== "unknown" && pace.level !== "ok" && (
        <div style={{ fontSize: 10.5, color: tone, fontWeight: 600, marginTop: 1 }}>
          {pace.burnGap >= pace.scheduleGap
            ? `burn ${pace.burnGap > 0 ? "+" : ""}${pace.burnGap} vs scope`
            : `calendar ${pace.scheduleGap > 0 ? "+" : ""}${pace.scheduleGap} vs scope`}
        </div>
      )}
    </div>
  );
}

const headerCell = {
  fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: T.soft,
  fontWeight: 700, textAlign: "left", padding: "0 10px 8px", whiteSpace: "nowrap",
  cursor: "pointer", userSelect: "none",
};
const cell = { padding: "11px 10px", borderTop: `1px solid ${T.line}`, verticalAlign: "top" };

/* A clickable figure. The drill is the whole point: the number opens the rows
   behind it where you are standing. */
function Fig({ onClick, children, muted }) {
  return (
    <button onClick={onClick} disabled={!onClick} style={{
      background: "none", border: "none", padding: 0, textAlign: "left",
      cursor: onClick ? "pointer" : "default", font: "inherit",
      color: muted ? T.soft : T.ink, textDecoration: onClick ? "underline dotted transparent" : "none",
    }}>{children}</button>
  );
}

const COLUMNS = [
  { key: "name", label: "Program" },
  { key: "status", label: "Status" },
  { key: "funded", label: "Funded / ceiling" },
  { key: "margin", label: "Margin" },
  { key: "pace", label: "Burn vs time vs scope" },
  { key: "aged", label: "Aged receivables" },
  { key: "docs", label: "Docs / compliance" },
  { key: "decisions", label: "Decisions" },
  { key: "flags", label: "Flags" },
];

/* How each column sorts. Posture rank first by default so red floats up. */
const sortValue = (s, key) => {
  switch (key) {
    case "name": return s.contract.name?.toLowerCase() ?? "";
    case "status": return s.posture.rank;
    case "funded": return s.ceiling ? s.spent / s.ceiling : -1;
    case "margin": return s.margin.pct === null ? -999 : s.margin.pct;
    case "pace": return Math.max(s.pace.burnGap ?? -999, s.pace.scheduleGap ?? -999);
    case "aged": return s.pastTermsDollars || 0;
    case "docs": return s.compliance.pct === null ? 999 : s.compliance.pct;
    case "decisions": return s.openDecisions;
    case "flags": return s.overdueCDs * 10 + s.highRisks;
    default: return 0;
  }
};

export default function PortfolioOverview({ contracts = [], onOpenContract }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "status", dir: "desc" });
  const [client, setClient] = useState("all");
  const [funding, setFunding] = useState("all");
  const [drill, setDrill] = useState(null);

  const ids = useMemo(() => contracts.map((c) => c.id), [contracts]);

  useEffect(() => {
    let live = true;
    setRows(null);
    if (!ids.length) { setRows({}); return undefined; }
    loadPortfolioRows(ids)
      .then((r) => { if (live) setRows(r); })
      .catch((e) => { if (live) setError(e.message || "Could not load the portfolio."); });
    return () => { live = false; };
  }, [ids]);

  const summaries = useMemo(
    () => (rows ? contracts.map((c) => programSummary(c, rows[c.id])) : null),
    [rows, contracts]);

  const clients = useMemo(() => summaries
    ? [...new Set(summaries.map((s) => s.contract.client).filter(Boolean))].sort() : [],
    [summaries]);
  const fundings = useMemo(() => summaries
    ? [...new Set(summaries.map((s) => s.fundingSource).filter(Boolean))].sort() : [],
    [summaries]);

  const shown = useMemo(() => {
    if (!summaries) return null;
    let list = summaries.filter((s) =>
      (client === "all" || s.contract.client === client)
      && (funding === "all" || s.fundingSource === funding));
    const dir = sort.dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = sortValue(a, sort.key), bv = sortValue(b, sort.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      /* Red stays above amber above green on any tie, then name. */
      return b.posture.rank - a.posture.rank
        || a.contract.name.localeCompare(b.contract.name);
    });
    return list;
  }, [summaries, client, funding, sort]);

  const totals = useMemo(() => (shown ? rollupTotals(shown) : null), [shown]);

  if (contracts.length === 0) {
    return <Panel title="Portfolio"><Empty text="No contracts to show yet." /></Panel>;
  }
  if (error) return <Panel title="Portfolio"><Empty text={error} /></Panel>;
  if (!summaries) return <Empty text="Loading the portfolio..." />;

  const openDrill = (s, kind, title, data) =>
    (data && data.length ? () => setDrill({ kind, title: `${s.contract.name}: ${title}`, rows: data }) : null);

  const th = (col) => (
    <th key={col.key} style={headerCell}
      onClick={() => setSort((p) => ({ key: col.key, dir: p.key === col.key && p.dir === "desc" ? "asc" : "desc" }))}>
      {col.label}{sort.key === col.key ? (sort.dir === "desc" ? " ▾" : " ▴") : ""}
    </th>
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <DrillPanel drill={drill} onClose={() => setDrill(null)} />

      {/* Portfolio band: the whole book of work in a line. */}
      <div style={{ background: T.navy, borderRadius: 8, padding: "18px 22px",
        display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
        <TotalFig label="Programs" value={totals.programs} />
        <TotalFig label="Health" value={<span>
          <span style={{ color: STATUS.complete.fill }}>{totals.green}</span>{" · "}
          <span style={{ color: STATUS.inProgress.fill }}>{totals.amber}</span>{" · "}
          <span style={{ color: STATUS.overdue.fill }}>{totals.red}</span>
        </span>} sub="green · watch · attention" />
        <TotalFig label="Portfolio value" value={totals.value ? fmtK(totals.value) : "n/a"}
          sub={`${fmtK(totals.spent)} committed`} />
        <TotalFig label="Forecast variance"
          value={totals.variance === null ? "n/a" : fmtK(Math.abs(totals.variance))}
          sub={totals.variance === null ? "too early" : totals.variance < 0 ? "projected over" : "projected under"} />
        <TotalFig label="Aged receivables" value={fmtK(totals.pastTermsDollars)}
          sub="past terms" />
        <TotalFig label="People" value={totals.fte !== null ? `${totals.fte} FTE` : totals.headcount}
          sub={`${totals.headcount} assigned`} />
      </div>

      {/* Filters, because a leader cuts the portfolio by funding source and client. */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Select label="Client" value={client} onChange={setClient} options={clients} />
        <Select label="Funding source" value={funding} onChange={setFunding} options={fundings} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: T.soft }}>
          {shown.length} of {summaries.length} programs
        </span>
      </div>

      <div style={{ overflowX: "auto", border: `1px solid ${T.line}`, borderRadius: 8,
        background: T.panel }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
          <thead><tr>{COLUMNS.map(th)}</tr></thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.contract.id}>
                {/* Program */}
                <td style={{ ...cell, minWidth: 180 }}>
                  <button onClick={() => onOpenContract?.(s.contract.id)} style={{
                    background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: T.navy,
                      overflowWrap: "anywhere" }}>{s.contract.name}</div>
                  </button>
                  <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>
                    {s.contract.client || "client not set"}
                    {s.contract.territory ? ` · ${s.contract.territory}` : ""}
                  </div>
                  {s.pm && (
                    <div style={{ fontSize: 11, color: T.steel, marginTop: 1 }}>PM: {s.pm}</div>
                  )}
                  {s.fundingSource && (
                    <span style={{ display: "inline-block", marginTop: 4, fontSize: 10,
                      fontWeight: 700, color: T.steel, background: T.chip, borderRadius: 4,
                      padding: "1px 6px" }}>{s.fundingSource}</span>
                  )}
                </td>

                {/* Status */}
                <td style={cell}><Pill posture={s.posture} /></td>

                {/* Funded / ceiling */}
                <td style={{ ...cell, minWidth: 130 }}>
                  {s.ceiling ? (
                    <>
                      <div style={{ fontSize: 13, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                        {s.funded ? `${fmtK(s.funded)} funded` : `${fmtK(s.ceiling)} ceiling`}
                      </div>
                      <div style={{ fontSize: 11, color: T.soft }}>
                        {s.budget.percentUsed}% of ceiling used
                      </div>
                      {s.funded && s.spent > s.funded && (
                        <div style={{ fontSize: 10.5, fontWeight: 700,
                          color: s.modInFlight ? STATUS.inProgress.ink : STATUS.overdue.ink }}>
                          {s.modInFlight ? "over funded, mod in flight" : "over funded, no mod"}
                        </div>
                      )}
                    </>
                  ) : <span style={{ fontSize: 11.5, color: T.soft }}>no ceiling set</span>}
                </td>

                {/* Gross margin from the Workday snapshot */}
                <td style={{ ...cell, minWidth: 90 }}>
                  {s.margin.pct === null ? (
                    <span style={{ fontSize: 11.5, color: T.soft }}>not set</span>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                        color: s.margin.pct < 10 ? STATUS.overdue.ink
                          : s.margin.pct < 25 ? STATUS.inProgress.ink : STATUS.complete.ink }}>
                        {s.margin.pct}%
                      </div>
                      <div style={{ fontSize: 10.5, color: T.soft }}>
                        {fmtK(s.margin.profit)} GP
                        {s.margin.asOf ? ` · ${s.margin.asOf}` : ""}
                      </div>
                    </>
                  )}
                </td>

                {/* Burn vs time vs scope — drills to the deliverables (the scope) */}
                <td style={cell}>
                  <Fig onClick={openDrill(s, "deliverables", "deliverables", rows[s.contract.id]?.deliverables)}>
                    <PaceCell pace={s.pace} />
                  </Fig>
                </td>

                {/* Aged receivables — drills to the invoices */}
                <td style={{ ...cell, minWidth: 110 }}>
                  {s.pastTermsCount ? (
                    <Fig onClick={openDrill(s, "invoices", "invoices past terms",
                      s.pastInvoices?.map((w) => ({ ...w.invoice, _aging: w.aging })))}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: STATUS.overdue.ink,
                        fontVariantNumeric: "tabular-nums" }}>{fmtK(s.pastTermsDollars)}</div>
                      <div style={{ fontSize: 11, color: T.soft }}>
                        {s.pastTermsCount} invoice{s.pastTermsCount === 1 ? "" : "s"}
                      </div>
                    </Fig>
                  ) : <span style={{ fontSize: 11.5, color: T.soft }}>clean</span>}
                </td>

                {/* Docs / compliance — drills to the compliance register */}
                <td style={{ ...cell, minWidth: 110 }}>
                  {s.compliance.total === 0 ? (
                    <span style={{ fontSize: 11.5, color: T.soft }}>none set</span>
                  ) : (
                    <Fig onClick={openDrill(s, "compliance_items", "compliance requirements",
                      rows[s.contract.id]?.compliance_items)}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                        color: s.compliance.level === "red" ? STATUS.overdue.ink
                          : s.compliance.level === "amber" ? STATUS.inProgress.ink : STATUS.complete.ink }}>
                        {s.compliance.pct}%
                      </div>
                      <div style={{ fontSize: 11, color: T.soft }}>
                        {s.compliance.complete}/{s.compliance.total} clean
                        {s.compliance.problems ? `, ${s.compliance.problems} flagged` : ""}
                      </div>
                    </Fig>
                  )}
                </td>

                {/* Decisions pending — drills to the decisions register */}
                <td style={cell}>
                  {s.openDecisions ? (
                    <Fig onClick={openDrill(s, "decisions", "open decisions", s.decisions)}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.ink,
                        fontVariantNumeric: "tabular-nums" }}>{s.openDecisions}</div>
                      <div style={{ fontSize: 11, color: T.soft }}>pending</div>
                    </Fig>
                  ) : <span style={{ fontSize: 11.5, color: T.soft }}>none</span>}
                </td>

                {/* Flags */}
                <td style={{ ...cell, minWidth: 100 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {s.overdueCDs > 0 && <FlagText text={`${s.overdueCDs} overdue CD`} />}
                    {s.highRisks > 0 && <FlagText text={`${s.highRisks} high risk`} />}
                    {s.sensitivity && s.sensitivity !== "Low" && (
                      <FlagText text={`${s.sensitivity} exposure`}
                        color={s.sensitivity === "High" ? STATUS.overdue.ink : STATUS.inProgress.ink} />
                    )}
                    {s.overdueCDs === 0 && s.highRisks === 0 && (!s.sensitivity || s.sensitivity === "Low") && (
                      <span style={{ fontSize: 11.5, color: T.soft }}>clear</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11.5, color: T.soft, textAlign: "center", lineHeight: 1.6 }}>
        Live from the registers, as of {today()}. Every figure opens the rows it was counted from.
        Forecast and burn gap are straight-line estimates, not formal earned value.
      </div>
    </div>
  );
}

function TotalFig({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, letterSpacing: "0.07em", textTransform: "uppercase",
        color: "#C9D2E0", fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 700, color: "#fff",
        marginTop: 3, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#9FB0C8", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ fontSize: 12, color: T.soft, display: "flex", alignItems: "center", gap: 6 }}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{
        border: `1px solid ${T.line}`, borderRadius: 5, padding: "5px 8px", fontSize: 12.5,
        background: "#fff", color: T.ink }}>
        <option value="all">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

const FlagText = ({ text, color = STATUS.overdue.ink }) => (
  <span style={{ fontSize: 10.5, fontWeight: 600, color }}>{text}</span>
);
